import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { productApi, categoryApi, labelApi } from '../shared/api/endpoints';
import { useWishlist } from '../shared/context/WishlistContext';
import { useAddToCart } from '../shared/hooks/useAddToCart';
import './ProductsPage.css';

function resolveMain(mains, raw) {
  if (!raw) return null;
  if (/^[a-f\d]{24}$/i.test(raw)) return mains.find((c) => c._id === raw) || null;
  const key = raw.toLowerCase();
  return mains.find((c) => c.slug === key || c.name.toLowerCase() === key) || null;
}

function FilterGroup({ id, title, open, onToggle, children }) {
  return (
    <div className="shop-acc">
      <button type="button" className="shop-acc__title" onClick={() => onToggle(id)}>
        <span>{title}</span>
        <em className={open ? 'is-open' : ''}>▾</em>
      </button>
      {open && <div className="shop-acc__body">{children}</div>}
    </div>
  );
}

export default function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [mains, setMains] = useState([]);
  const [subs, setSubs] = useState([]);
  const [labels, setLabels] = useState([]);
  const [facets, setFacets] = useState({ sizes: [], fabricTypes: [], colours: [] });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({
    all: true,
    collections: true,
    sizes: true,
    fabric: true,
    colour: true,
  });
  const [sizes, setSizes] = useState({});
  const [hoverId, setHoverId] = useState(null);
  const addToCart = useAddToCart();
  const { toggle, has } = useWishlist();

  const categoryParam = params.get('category') || '';
  const subParam = params.get('subCategory') || '';
  const labelParam = params.get('label') || '';
  const dealParam = params.get('isDeal') || '';
  const qParam = params.get('q') || '';
  const sizeParam = params.get('size') || '';
  const fabricParam = params.get('fabricType') || '';
  const colourParam = params.get('colour') || '';

  useEffect(() => {
    Promise.all([
      categoryApi.list({ type: 'main' }),
      categoryApi.list({ type: 'sub' }),
      labelApi.list(),
      productApi.facets(),
    ])
      .then(([m, s, l, f]) => {
        const mainList = m.data.data || [];
        setMains(mainList);
        setSubs(s.data.data || []);
        setLabels(l.data.data || []);
        setFacets(f.data.data || { sizes: [], fabricTypes: [], colours: [] });
        const open = {
          all: true,
          collections: true,
          sizes: true,
          fabric: true,
          colour: true,
        };
        mainList.forEach((c) => {
          open[c._id] = true;
        });
        setOpenGroups(open);
      })
      .catch(() => {});
  }, []);

  const activeMain = useMemo(() => {
    const fromParam = resolveMain(mains, categoryParam);
    if (fromParam) return fromParam;
    if (subParam) {
      const sub = subs.find((s) => s._id === subParam);
      const parentId = String(sub?.parent?._id || sub?.parent || '');
      return mains.find((m) => m._id === parentId) || null;
    }
    return null;
  }, [mains, categoryParam, subParam, subs]);

  const activeLabel = useMemo(
    () => labels.find((l) => l._id === labelParam) || null,
    [labels, labelParam]
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const q = {
          label: labelParam || undefined,
          isDeal: dealParam || undefined,
          q: qParam || undefined,
          size: sizeParam || undefined,
          fabricType: fabricParam || undefined,
          colour: colourParam || undefined,
        };
        if (activeMain) q.category = activeMain._id;
        else if (categoryParam && /^[a-f\d]{24}$/i.test(categoryParam)) {
          q.category = categoryParam;
        }
        if (subParam) q.subCategory = subParam;

        const res = await productApi.list(q);
        if (!cancelled) setData(res.data.data || { items: [], total: 0 });
      } catch {
        if (!cancelled) setData({ items: [], total: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [
    categoryParam,
    subParam,
    labelParam,
    dealParam,
    qParam,
    sizeParam,
    fabricParam,
    colourParam,
    activeMain,
  ]);

  const patchParams = (mutate) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next);
    setFiltersOpen(false);
  };

  const setSub = (id, parentId) => {
    patchParams((next) => {
      if (!id) next.delete('subCategory');
      else next.set('subCategory', id);
      if (parentId) next.set('category', parentId);
    });
  };

  const setMain = (id) => {
    patchParams((next) => {
      if (id) next.set('category', id);
      else next.delete('category');
      next.delete('subCategory');
    });
  };

  const setLabel = (id) => {
    patchParams((next) => {
      if (!id || labelParam === id) next.delete('label');
      else next.set('label', id);
    });
  };

  const toggleParam = (key, value, current) => {
    patchParams((next) => {
      if (!value || current === value) next.delete(key);
      else next.set(key, value);
    });
  };

  const hasActiveFilters = Boolean(
    activeLabel ||
      activeMain ||
      subParam ||
      dealParam === 'true' ||
      sizeParam ||
      fabricParam ||
      colourParam
  );

  const clearAllFilters = () => {
    setParams(new URLSearchParams());
    setFiltersOpen(false);
  };

  const toggleGroup = (id) => {
    setOpenGroups((g) => ({ ...g, [id]: !g[id] }));
  };

  const pickSize = (productId, size) => {
    setSizes((s) => ({ ...s, [productId]: size }));
  };

  const sizeFor = (p) => sizes[p._id] || p.sizesAvailable?.[0] || '';

  const countLabel = loading
    ? 'Loading…'
    : [
        `${data.total} piece${data.total === 1 ? '' : 's'}`,
        activeLabel?.name,
        activeMain?.name,
        sizeParam,
        fabricParam,
        colourParam,
      ]
        .filter(Boolean)
        .join(' · ');

  const filterPanel = (
    <aside className="shop-side">
      <div className="shop-side__head">
        <div>
          <p>Filters</p>
          <span className="shop-side__count">{countLabel}</span>
        </div>
        <div className="shop-side__actions">
          {hasActiveFilters && (
            <button type="button" className="shop__clear shop__clear--side" onClick={clearAllFilters}>
              Clear
            </button>
          )}
          <button type="button" className="shop-side__close" onClick={() => setFiltersOpen(false)}>
            Close
          </button>
        </div>
      </div>

      <FilterGroup id="all" title="Browse" open={openGroups.all} onToggle={toggleGroup}>
        <label className="shop-check">
          <span>All pieces</span>
          <input
            type="checkbox"
            checked={!hasActiveFilters}
            onChange={clearAllFilters}
          />
          <i />
        </label>
      </FilterGroup>

      {labels.length > 0 && (
        <FilterGroup
          id="collections"
          title="Collections"
          open={openGroups.collections}
          onToggle={toggleGroup}
        >
          {labels.map((l) => (
            <label key={l._id} className="shop-check">
              <span>{l.name}</span>
              <input
                type="checkbox"
                checked={labelParam === l._id}
                onChange={() => setLabel(l._id)}
              />
              <i />
            </label>
          ))}
        </FilterGroup>
      )}

      {mains.map((c) => {
        const children = subs.filter(
          (s) => String(s.parent?._id || s.parent) === String(c._id)
        );
        const mainChecked = activeMain?._id === c._id && !subParam;
        return (
          <FilterGroup
            key={c._id}
            id={c._id}
            title={c.name}
            open={openGroups[c._id]}
            onToggle={toggleGroup}
          >
            <label className="shop-check">
              <span>All {c.name}</span>
              <input
                type="checkbox"
                checked={mainChecked}
                onChange={() => setMain(c._id)}
              />
              <i />
            </label>
            {children.map((s) => (
              <label key={s._id} className="shop-check">
                <span>{s.name}</span>
                <input
                  type="checkbox"
                  checked={subParam === s._id}
                  onChange={() => setSub(subParam === s._id ? '' : s._id, c._id)}
                />
                <i />
              </label>
            ))}
          </FilterGroup>
        );
      })}

      {facets.sizes.length > 0 && (
        <FilterGroup id="sizes" title="Size" open={openGroups.sizes} onToggle={toggleGroup}>
          {facets.sizes.map((s) => (
            <label key={s} className="shop-check">
              <span>{s}</span>
              <input
                type="checkbox"
                checked={sizeParam === s}
                onChange={() => toggleParam('size', s, sizeParam)}
              />
              <i />
            </label>
          ))}
        </FilterGroup>
      )}

      {facets.fabricTypes.length > 0 && (
        <FilterGroup
          id="fabric"
          title="Fabric type"
          open={openGroups.fabric}
          onToggle={toggleGroup}
        >
          {facets.fabricTypes.map((f) => (
            <label key={f} className="shop-check">
              <span>{f}</span>
              <input
                type="checkbox"
                checked={fabricParam.toLowerCase() === f.toLowerCase()}
                onChange={() => toggleParam('fabricType', f, fabricParam)}
              />
              <i />
            </label>
          ))}
        </FilterGroup>
      )}

      {facets.colours.length > 0 && (
        <FilterGroup id="colour" title="Colour" open={openGroups.colour} onToggle={toggleGroup}>
          {facets.colours.map((c) => (
            <label key={c} className="shop-check">
              <span>{c}</span>
              <input
                type="checkbox"
                checked={colourParam.toLowerCase() === c.toLowerCase()}
                onChange={() => toggleParam('colour', c, colourParam)}
              />
              <i />
            </label>
          ))}
        </FilterGroup>
      )}
    </aside>
  );

  return (
    <div className="page-shell">
      <Navbar />
      <main className="shop">
        <div className="shop__layout">
          <div className={`shop__rail${filtersOpen ? ' is-open' : ''}`}>{filterPanel}</div>
          {filtersOpen && (
            <button
              type="button"
              className="shop__backdrop"
              aria-label="Close filters"
              onClick={() => setFiltersOpen(false)}
            />
          )}

          <div className="shop__content">
            <div className="shop__toolbar">
              <button
                type="button"
                className="shop__filter-btn"
                onClick={() => setFiltersOpen(true)}
              >
                Filters
                <em>{loading ? '…' : data.total}</em>
              </button>
              {hasActiveFilters && (
                <button type="button" className="shop__clear" onClick={clearAllFilters}>
                  Clear
                </button>
              )}
            </div>

            {!loading && data.items.length === 0 && (
              <div className="shop__empty">
                <p>Nothing in this edit yet.</p>
                <Link to="/" className="btn btn-gold">
                  Back home
                </Link>
              </div>
            )}

            <div className="shop__grid">
              {data.items.map((p) => {
                const active = hoverId === p._id;
                const selected = sizeFor(p);
                return (
                  <article
                    key={p._id}
                    className={`pcard${active ? ' is-active' : ''}`}
                    onMouseEnter={() => setHoverId(p._id)}
                    onMouseLeave={() => setHoverId(null)}
                  >
                    <Link
                      to={`/products/${p._id}`}
                      className="pcard__media"
                      style={{
                        backgroundImage: p.images?.[0] ? `url(${p.images[0]})` : undefined,
                      }}
                      aria-label={p.title}
                    >
                      {p.discount > 0 && (
                        <span className="pcard__badge">
                          <em>−{p.discount}%</em>
                          <b>off</b>
                        </span>
                      )}
                      <button
                        type="button"
                        className={`pcard__wish${has(p._id) ? ' is-on' : ''}`}
                        aria-label="Wishlist"
                        onClick={(e) => {
                          e.preventDefault();
                          toggle(p);
                        }}
                      >
                        ♥
                      </button>
                    </Link>

                    <div className="pcard__panel">
                      <Link to={`/products/${p._id}`} className="pcard__title">
                        <h3>{p.title}</h3>
                      </Link>
                      <p className="pcard__sub">
                        {p.subtitle || p.description || 'Handpicked from the atelier.'}
                      </p>

                      <div className="pcard__mid">
                        {p.sizesAvailable?.length > 0 ? (
                          <div className="pcard__sizes" role="group" aria-label="Size">
                            {p.sizesAvailable.map((s) => (
                              <button
                                key={s}
                                type="button"
                                className={selected === s ? 'is-on' : ''}
                                onClick={() => pickSize(p._id, s)}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className="pcard__onesize">One size</span>
                        )}
                        <div className="pcard__price">
                          <strong>₹{Number(p.sellingPrice).toLocaleString()}</strong>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="pcard__cart"
                        onClick={() => addToCart(p, selected)}
                      >
                        Add to cart
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
